/**
 * Tweet image loading for the palace canvases.
 *
 * About a third of the archive carries media, all of it on `pbs.twimg.com`
 * (video saves point at their poster frame, so everything here is a still).
 * The export's `local_path` is a stale container path with no mirror in this
 * repo, so the images come off the network — which makes this module's job
 * mostly restraint: a bounded LRU so a long walk can't retain thousands of
 * decoded bitmaps, a small concurrency window so a room's worth of frames
 * doesn't open forty sockets at once, and a permanent memo of failures so a
 * dead URL is never retried when its wall streams back in.
 *
 * `crossOrigin = 'anonymous'` is mandatory, not hygiene: drawing an image
 * without it taints the canvas, and uploading a tainted canvas as a WebGL
 * texture throws. Twitter's CDN reflects the origin, so this works; anything
 * that doesn't resolves to null and the card stays text-only.
 */

const MAX_CACHED = 180; // decoded images held at once
const MAX_CONCURRENT = 4; // sockets open at once

const cache = new Map(); // url -> HTMLImageElement, insertion-ordered (LRU)
const failed = new Set(); // urls that will never load
const inFlight = new Map(); // url -> Promise<HTMLImageElement|null>
const waiting = []; // queued { url, resolve }
let active = 0;

// pbs serves a `name` variant of every still. Full size is up to 4096px for a
// card that is at most a third of a 2048px canvas, so ask for the small one.
const sizedUrl = (url, variant) =>
  (url.includes('?') ? url : `${url}?format=jpg&name=${variant}`);

const isUsable = (m) =>
  m && typeof m.url === 'string' && /^https:\/\//.test(m.url) && m.download_status !== 'failed';

/**
 * The still to hang with a save, or null. Photos win over video posters —
 * a real photo is the better card either way.
 */
export const mediaForContent = (content) => {
  const list = content?.medias;
  if (!Array.isArray(list) || !list.length) return null;
  const usable = list.filter(isUsable);
  if (!usable.length) return null;
  const photo = usable.find((m) => m.type === 'photo');
  const chosen = photo || usable[0];
  return { url: chosen.url, isVideo: chosen.type !== 'photo' };
};

const touch = (url) => {
  const image = cache.get(url);
  cache.delete(url);
  cache.set(url, image);
  return image;
};

const evict = () => {
  while (cache.size > MAX_CACHED) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
};

let pump; // hoisted: a settling job pumps the queue again

const launch = (job) => {
  active++;
  const image = new Image();
  image.crossOrigin = 'anonymous';
  const settle = (result) => {
    active--;
    inFlight.delete(job.url);
    if (result) {
      cache.set(job.url, result);
      evict();
    } else {
      failed.add(job.url);
    }
    job.resolve(result);
    pump();
  };
  image.onload = () => settle(image.naturalWidth ? image : null);
  image.onerror = () => settle(null);
  image.src = job.src;
};

pump = () => {
  while (active < MAX_CONCURRENT && waiting.length) launch(waiting.shift());
};

/**
 * Load (or return cached) media for a URL. Never rejects: a failure — CORS,
 * 404, a deleted tweet's image — resolves to null and the caller draws text.
 */
export const loadMedia = (url, variant = 'small') => {
  if (!url || failed.has(url)) return Promise.resolve(null);
  if (cache.has(url)) return Promise.resolve(touch(url));
  if (inFlight.has(url)) return inFlight.get(url);
  const promise = new Promise((resolve) => {
    waiting.push({ url, src: sizedUrl(url, variant), resolve });
    pump();
  });
  inFlight.set(url, promise);
  return promise;
};

export const cachedMedia = (url) => (url && cache.has(url) ? touch(url) : null);

// Drop the bitmaps on museum teardown; the failure memo is worth keeping for
// a second visit, and costs a few hundred strings.
export const clearMediaCache = () => {
  cache.clear();
  waiting.length = 0;
};
